/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable arrow-body-style */
import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Autocomplete } from '@material-ui/lab';
import Button from '../../components/Button';
import TextInput from '../../components/TextInput';
import Form from '../../components/Form';
import { StyledText } from '../../components/StyledText';
import { useUser } from '../../contexts';
import EmailChip from '../../components/EmailChip';

const validationSchema = Yup.object().shape({
  inviteMessage: Yup.string().required(),
  entEmails: Yup.array().of(Yup.string().email()).required().min(1)
});


interface InviteEntsModalProps {
  open: boolean
  defaultMessage: string
  onClose: () => void
  handleSubmit: (values: any) => void
}

const InviteEntsModal: React.FC<InviteEntsModalProps> = ({
  open,
  defaultMessage,
  onClose,
  handleSubmit
}) => {
  const { userInfo } = useUser();
  const emptyEmails: string[] = [];

  const initialValues = {
    inviteMessage: defaultMessage,
    entEmails: emptyEmails
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    // validateOnMount: true
    validateOnBlur: false,
    validateOnChange: true,
    enableReinitialize: true
  });

  useEffect(() => {
    if (open) {
      formik.resetForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);


  const checkIfEmailAtIndexValid = (idx: number) => {
    const emails = formik.values.entEmails;
    const scheme = Yup.string().email().required();
    try {
      scheme.validateSync(emails[idx]);
    } catch (validationError: unknown) {
      return false;
    }
    return true;
  };

  const parseInputIntoEmails = (input: string, editing: boolean = false) => {
    const split = input.split(',').map((email) => email.trim());

    let toAdd: string[] = [];
    if (editing) {
      if (split.length > 1) {
        toAdd = split.slice(0, split.length - 1);
        const newList = formik.values.entEmails.concat(toAdd);
        formik.setFieldValue('entEmails', newList);
      }
    } else {
      toAdd = split;
      const newList = formik.values.entEmails.concat(toAdd);
      formik.setFieldValue('entEmails', newList);
    }
  };

  const [emailFieldSelected, setEmailFieldSelected] = useState(false);
  const emailFieldLabel = useMemo(() => {
    if (emailFieldSelected) {
      return 'To (comma separated emails)';
    }
    return 'To';
  }, [emailFieldSelected]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
    >
      <Form formik={formik}>
        <DialogTitle>
          Invite an entrepreneur
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            id='entEmails'
            getOptionLabel={(option: string) => option}
            disableListWrap
            options={[]}
            value={formik.values.entEmails}
            onChange={(event, value, reason) => {
              if (reason === 'remove-option') {
                formik.setFieldValue('entEmails', value);
              } else if (reason === 'create-option') {
                const currentVals = formik.values.entEmails;
                const deduped = new Set(currentVals);
                value.forEach((v) => {
                  deduped.add(v.trim());
                });
                const newVals = Array.from(deduped);
                formik.setFieldValue('entEmails', newVals);
              } else if (reason === 'clear') {
                formik.setFieldValue('entEmails', []);
              }
            }}
            onBlur={formik.handleBlur}
            renderOption={(option) => (<StyledText color='secondary'>{option}</StyledText>)}
            freeSolo
            multiple
            autoSelect
            fullWidth
            renderInput={(params: any) => {
              return (
                <TextField
                  id='entEmails'
                  fullWidth
                  inputProps={params.inputProps}
                  InputLabelProps={params.InputLabelProps}
                  label={emailFieldLabel}
                  variant='outlined'
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  onChange={(event: any) => {
                    if (event.target && event.target.value) {
                      const val = event.target.value;
                      parseInputIntoEmails(val, true);
                    }
                  }}
                  onFocus={() => { setEmailFieldSelected(true); }}
                  onBlur={(event: any) => {
                    setEmailFieldSelected(false);
                    if (event.target && event.target.value) {
                      const val = event.target.value;
                      parseInputIntoEmails(val);
                    }
                  }}
                />
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const validState = checkIfEmailAtIndexValid(index);
                return (
                  <EmailChip
                    title={option}
                    valid={validState}
                    {...getTagProps({ index })}
                  />
                );
              })}
          />
          {formik.isSubmitting && (
            <div style={{ zIndex: 100, position: 'fixed', height: '200px', width: '200px', top: '50%', left: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginTop: '-100px', marginLeft: '-100px' }}>
              <CircularProgress />
            </div>
          )}
          <TextInput
            id='inviteMessage'
            label='Message to Entrepreneur'
            fullWidth
            type='text'
            placeholder=''
            multiline
            rows={0}
          />
        </DialogContent>
        <DialogActions>
          <Button type='button' variant='text' onClick={onClose}>Cancel</Button>
          <Button type='submit' variant='contained' disabled={!formik.isValid || !formik.values.inviteMessage || formik.values.entEmails.length === 0 || formik.isSubmitting}>Send</Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default InviteEntsModal;
